import {
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Heading,
  IconAddLine,
  IconArrowStartLine,
  Spinner,
  TextInput,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useState } from "react";
import { TemplateService } from "../../services/TemplateService";
import { sort } from "../../utils/utils";
import ResultsPreview from "../Assessment/Plan/ResultsPreview/ResultsPreview";
import { EMBEDDED, QUALITATIVE } from "../Assessment/Plan/utils";
import { Card } from "../Card";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { EMPTY_RTE } from "../CustomTextArea/CustomRTE";
import TemplateSectionCard from "./TemplateSectionCard";

const ManageTemplate = ({ template, navigateToPreviousPage, setTemplates }) => {
  const showNotification = useNotification();
  const [templateName, setTemplateName] = useState(template?.name ?? "");
  const [includeMission, setIncludeMission] = useState(
    !!template?.include_mission
  );
  const [includeGoalsObjs, setIncludeGoalsObjs] = useState(
    !!template?.include_goals_objs
  );
  const [sections, setSections] = useState(template?.sections ?? []);

  const [runningIndex, setRunningIndex] = useState(
    template?.sections.length ?? 0
  );
  const [creating, setCreating] = useState(false);

  const templateVerification = () => {
    let valid = true;
    sections.forEach((section) => {
      if (section.name === "") {
        showNotification(
          t("assessment.planRequirements.sectionHeadingRequired"),
          ERROR
        );
        valid = false;
        return;
      } else if (
        section.description === EMPTY_RTE ||
        section.description === ""
      ) {
        showNotification(
          t("assessment.planRequirements.sectionDescriptionRequired"),
          ERROR
        );
        valid = false;
        return;
      } else if (section.measures.length === 0) {
        showNotification(
          t("assessment.planRequirements.oneMeasureRequired"),
          ERROR
        );
        valid = false;
        return;
      }
      section.measures.forEach((measure) => {
        if (measure.type === QUALITATIVE) {
          if (measure.description && measure.description !== EMPTY_RTE) {
            measure.instruction = measure.description;
          }
          if (measure.name === "") {
            showNotification(
              t("assessment.planRequirements.measureNameRequired"),
              ERROR
            );
            valid = false;
            return;
          }
        } else if (measure.type === EMBEDDED) {
          if (measure.name === "") {
            showNotification(
              t("assessment.planRequirements.embeddedNameRequired"),
              ERROR
            );
            valid = false;
            return;
          }
          if (measure.description === "") {
            showNotification(
              t("assessment.planRequirements.embeddedDescriptionRequired"),
              ERROR
            );
            valid = false;
            return;
          }
        }
      });
    });
    return valid;
  };

  const handleSaveTemplate = () => {
    let validTemplate = templateVerification();
    if (validTemplate) {
      setCreating(true);
      TemplateService.upsertTemplate({
        id: template?.id,
        name: templateName,
        include_mission: includeMission,
        include_goals_objs: includeGoalsObjs,
        sections: sections,
      })
        .then((newTemplate) => {
          newTemplate.sections.forEach((section, i) => {
            section.index = i;
            section.measures.forEach((measure, j) => {
              measure.index = j;
            });
          });
          if (template?.id) {
            setTemplates((prevTemplates) =>
              prevTemplates.map((t) =>
                t.id === newTemplate.id ? newTemplate : t
              )
            );
            showNotification(
              t("notifications.general.updateSuccess", { obj: "Template" })
            );
          } else {
            setTemplates((prevTemplates) =>
              sort([...prevTemplates, newTemplate], (t) => t.name)
            );
            showNotification(
              t("notifications.general.createSuccess", { obj: "Template" })
            );
          }
          navigateToPreviousPage();
        })
        .catch(() => {
          showNotification(
            t("notifications.general.createError", { obj: "Templates" }),
            ERROR
          );
        })
        .finally(() => setCreating(false));
    }
  };

  const handleAddSection = () => {
    setSections((prevSections) => [
      ...prevSections,
      { index: runningIndex, name: "", description: "", measures: [] },
    ]);
    setRunningIndex(runningIndex + 1);
  };

  return (
    <div
      style={{
        margin: "-0.75rem",
        height: "calc(100vh - 2.375rem - 1.5rem - 3rem - 1.375rem)",
      }}
    >
      <View as="div" height="calc(100vh - 2.375rem - 1.5rem - 3rem - 0.75rem)">
        <div
          style={{
            margin: "0.75rem 0.75rem 0 0.75rem",
            borderBottom: "1px solid #ccc",
          }}
        >
          <Heading as="h2" level="h3">
            {template?.id
              ? t("templates.editTemplate")
              : t("templates.createTemplate")}
          </Heading>
        </div>
        <Flex direction="column" height="100%">
          <Flex.Item padding="small">
            <TextInput
              id="template-name"
              renderLabel={t("templates.templateName")}
              defaultValue={templateName}
              onBlur={(e) => setTemplateName(e.target.value)}
            />
          </Flex.Item>
          <Flex.Item padding="0 small small small">
            <CheckboxGroup
              name="headers"
              description={t("templates.include") + ":"}
              layout="columns"
              defaultValue={[
                includeMission ? "mission" : "",
                includeGoalsObjs ? "goalsObjs" : "",
              ]}
            >
              <Checkbox
                label={t("assessment.mission")}
                value="mission"
                checked={includeMission}
                onChange={(e) => setIncludeMission(e.target.checked)}
              />
              <Checkbox
                label={t("dashboard.goalsAndObjectives")}
                value="goalsObjs"
                checked={includeGoalsObjs}
                onChange={(e) => setIncludeGoalsObjs(e.target.checked)}
              />
            </CheckboxGroup>
          </Flex.Item>
          <Flex.Item padding="0 small">
            <Heading as="h2" level="h3">
              {t("assessment.sections.sections")}:
            </Heading>
          </Flex.Item>
          <Flex.Item padding="0 small">
            {sections.map((section, key) => (
              <Card
                key={section.index}
                style={{
                  backgroundColor: "#F1F1F1",
                  border: "1px solid #ccc",
                  height: "auto",
                  margin: "0.75rem 0",
                }}
              >
                <TemplateSectionCard
                  index={key}
                  lastSection={key === sections.length - 1}
                  section={section}
                  setSections={setSections}
                />
              </Card>
            ))}
          </Flex.Item>
          <Flex.Item padding="small" shouldGrow>
            <Button
              color="primary"
              display="block"
              renderIcon={IconAddLine}
              onClick={handleAddSection}
            >
              {t("assessment.sections.addSection")}
            </Button>
          </Flex.Item>
          <Flex.Item padding="0 small">
            <div style={{ borderBottom: "1px solid #ccc" }} />
          </Flex.Item>
          <Flex.Item textAlign="end" padding="0 small small small">
            <Flex
              textAlign="start"
              wrap="wrap"
              gap="small"
              padding="small 0 0 0"
            >
              <Flex.Item shouldGrow>
                <Button
                  renderIcon={IconArrowStartLine}
                  onClick={() => {
                    setTemplateName("");
                    setIncludeMission(false);
                    setIncludeGoalsObjs(false);
                    setSections([]);
                    navigateToPreviousPage();
                  }}
                >
                  {t("global.goBack")}
                </Button>
              </Flex.Item>
              <Flex.Item>
                <PreviewResultsButton
                  sections={sections}
                  includeMission={includeMission}
                  includeGoalsObjs={includeGoalsObjs}
                  templateVerification={templateVerification}
                />
              </Flex.Item>
              <Flex.Item>
                <Button
                  color="primary"
                  onClick={handleSaveTemplate}
                  interaction={
                    !!templateName && sections.length > 0 && !creating
                      ? "enabled"
                      : "disabled"
                  }
                >
                  {creating ? (
                    <Spinner
                      renderTitle={t("global.pleaseWait")}
                      size="x-small"
                    />
                  ) : template?.id ? (
                    t("templates.updateTemplate")
                  ) : (
                    t("templates.createTemplate")
                  )}
                </Button>
              </Flex.Item>
            </Flex>
          </Flex.Item>
        </Flex>
      </View>
    </div>
  );
};

export default ManageTemplate;

const PreviewResultsButton = ({
  sections,
  includeMission,
  includeGoalsObjs,
  templateVerification,
}) => {
  const [openPreview, setOpenPreview] = useState(false);

  return (
    <>
      <Button
        onClick={() => {
          if (templateVerification()) setOpenPreview(true);
        }}
        interaction={sections.length > 0 ? "enabled" : "disabled"}
      >
        {t("results.previewResults")}
      </Button>
      <ResultsPreview
        open={openPreview}
        setOpen={setOpenPreview}
        sections={sections}
        data={{ includeMission, includeGoalsObjs }}
      />
    </>
  );
};
